/**
 * Which brand a host belongs to (ORDR-164, over paio-ordering ORDR-161).
 *
 * `GET /ordering/flow/?entry=BRAND_DOMAIN&host=<hostname>`. `host` names the
 * brand ALONE: the server answers it with a 400 beside `brand` or `site`, so
 * neither is ever sent with it. Asked only on a host that is not one of the
 * app's own (`config/env.ts` `STAFF_HOSTS`).
 *
 * THREE ANSWERS, NEVER TWO. A 200 is the brand. A 404 is the server's one
 * neutral miss (unmapped host, unservable brand, malformed host alike) and
 * renders "not an ordering site". Anything else, including a 200 this parser
 * refuses, is a failure the diner can retry: an outage must not tell a diner
 * that a real brand's address is not an ordering site, and NEITHER answer is
 * ever the staff door.
 *
 * Only `brand_entry.brand_node_id` is read. The chooser reads the brand
 * directory for everything it draws (names, Sites, open state), because
 * `brand_entry.locations` carries no `site_id` and choosing needs one.
 */

import type { ApiClient } from "../api/client.js";

export type HostEntry =
  | { readonly kind: "brand"; readonly brandNodeId: string }
  | { readonly kind: "not-found" }
  | { readonly kind: "failed"; readonly correlationId: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function field(value: unknown, name: string): unknown {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)[name]
    : undefined;
}

export async function readHostEntry(client: ApiClient, hostname: string): Promise<HostEntry> {
  const query = new URLSearchParams({ entry: "BRAND_DOMAIN", host: hostname });
  const response = await client.request(`/ordering/flow/?${query.toString()}`);
  if (response.status === 404) {
    return { kind: "not-found" };
  }
  if (response.status !== 200) {
    return { kind: "failed", correlationId: response.correlationId };
  }
  const brandNodeId = field(field(field(response.body, "data"), "brand_entry"), "brand_node_id");
  if (typeof brandNodeId !== "string" || !UUID.test(brandNodeId)) {
    console.warn(`[entry] host read answer refused (${response.correlationId}): brand_entry.brand_node_id`);
    return { kind: "failed", correlationId: response.correlationId };
  }
  return { kind: "brand", brandNodeId: brandNodeId.toLowerCase() };
}
